import { getJson } from '@/lib/apiConfig';
import { getCopeiraOverride } from '@/utils/getCopeiraOverride';
import { roundMoney } from '@/utils/helpers';

const EVENT_SLUG_TO_API: Record<string, string> = {
  manha: 'Coffee Break - Manhã',
  tarde: 'Coffee Break - Tarde',
  almoco: 'Almoço',
  happy: 'Happy Hour',
  outros: 'Outros',
};

export function resolveEventoApiName(evento: {
  evento?: string;
  eventSlug?: string;
}): string {
  const raw = (evento.evento || '').trim();
  if (raw) {
    const mapped = EVENT_SLUG_TO_API[raw.toLowerCase()];
    return mapped || raw;
  }
  const slug = (evento.eventSlug || 'manha').toLowerCase();
  return EVENT_SLUG_TO_API[slug] || EVENT_SLUG_TO_API.manha;
}

export function calcUnitPriceFromFamiliaItem(product: any): number {
  const qtdTotal = Number(product.QtdTotal) || 0;
  const valorTotal = Number(product.ValorTotal) || 0;
  const valor = Number(product.Valor) || Number(product.valor) || 0;

  if (qtdTotal > 0 && valorTotal > 0) {
    return valorTotal / qtdTotal;
  }
  return valor;
}

function extractFamiliaFromProdutoRecord(record: any): string {
  if (!record) return '';
  return (
    record.Familia ||
    record.familia ||
    record.Sub_Familia ||
    record.sub_familia ||
    ''
  );
}

function resolveFamiliaHint(produto: any, detalhe?: any | null): string {
  const fromProduto = extractFamiliaFromProdutoRecord(produto);
  if (fromProduto) return fromProduto;
  return extractFamiliaFromProdutoRecord(detalhe);
}

async function fetchProdutoFromCatalog(
  produtoId: number,
  catalogCache: Map<number, any | null>,
  catalogPages: Map<number, any[]>
): Promise<any | null> {
  if (catalogCache.has(produtoId)) {
    return catalogCache.get(produtoId) ?? null;
  }

  const MAX_PAGES = 12;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    if (!catalogPages.has(page)) {
      try {
        const data = await getJson<any>('rEsr7KLt', 'produto', { page });
        const items = Array.isArray(data?.items) ? data.items : [];
        catalogPages.set(page, items);
        items.forEach((item: any) => {
          const id = Number(item?.id);
          if (id) catalogCache.set(id, item);
        });
        if (items.length === 0) break;
      } catch {
        break;
      }
    }

    if (catalogCache.has(produtoId)) {
      return catalogCache.get(produtoId) ?? null;
    }
  }

  catalogCache.set(produtoId, null);
  return null;
}

async function fetchFamiliaItems(
  familia: string,
  pessoas: number,
  evento: string,
  cache: Map<string, any[]>
): Promise<any[]> {
  const key = `${familia}|${pessoas}|${evento}`;
  if (cache.has(key)) {
    return cache.get(key) || [];
  }
  try {
    const data = await getJson<any>('rEsr7KLt', 'produtoFamilia', {
      page: 0,
      familia,
      pessoas,
      evento,
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    cache.set(key, items);
    return items;
  } catch {
    cache.set(key, []);
    return [];
  }
}

function toBoolAvailability(value: any): boolean {
  return (
    value === true ||
    value === 'Sim' ||
    value === 'sim' ||
    value === 1 ||
    value === '1'
  );
}

function isApiProductActive(record: any): boolean {
  if (!record) return false;

  const hasAtivo = Object.prototype.hasOwnProperty.call(record, 'Ativo');
  const hasVisivel = Object.prototype.hasOwnProperty.call(
    record,
    'Visivel_Cliente'
  );
  const hasDisp = Object.prototype.hasOwnProperty.call(
    record,
    'Disponivel_Venda'
  );

  if (!hasAtivo && !hasVisivel && !hasDisp) return true;

  const ativoOk = hasAtivo ? toBoolAvailability(record.Ativo) : true;
  const visivelOk = hasVisivel ? toBoolAvailability(record.Visivel_Cliente) : true;
  const dispOk = hasDisp ? toBoolAvailability(record.Disponivel_Venda) : true;

  return ativoOk && visivelOk && dispOk;
}

export function isGenericProdutoLabel(
  nome: string | undefined,
  produtoId: number
): boolean {
  const n = (nome || '').trim();
  return (
    !n ||
    n === `Produto #${produtoId}` ||
    n === `Produto ${produtoId}` ||
    n.startsWith(`Produto removido do catálogo (#${produtoId})`)
  );
}

export function formatNomeProdutoIndisponivel(
  nome: string | undefined,
  produtoId: number
): string {
  if (isGenericProdutoLabel(nome, produtoId)) {
    return `Produto removido do catálogo (#${produtoId})`;
  }
  const base = (nome || '').trim();
  if (base.includes('(indisponível)')) return base;
  return `${base} (indisponível)`;
}

type ProdutoDuplicacaoResolve = {
  unitPrice: number | null;
  disponivel: boolean;
  familia: string;
  nome: string;
  image: string;
};

async function resolveProdutoDuplicacao(
  produto: any,
  pessoas: number,
  evento: string,
  catalogCache: Map<number, any | null>,
  catalogPages: Map<number, any[]>,
  familiaCache: Map<string, any[]>
): Promise<ProdutoDuplicacaoResolve> {
  const produtoId = Number(produto?.produto_id);
  const nomeOriginal = produto?.nome || produto?.name || '';
  const imageOriginal = produto?.image || produto?.imagem || '';

  if (!produtoId) {
    return {
      unitPrice: null,
      disponivel: false,
      familia: produto?.familia || '',
      nome: formatNomeProdutoIndisponivel(nomeOriginal, 0),
      image: imageOriginal,
    };
  }

  let familia = resolveFamiliaHint(produto);
  let catalogRecord: any | null = null;

  if (!familia) {
    catalogRecord = await fetchProdutoFromCatalog(
      produtoId,
      catalogCache,
      catalogPages
    );
    familia = resolveFamiliaHint(produto, catalogRecord);
  }

  if (familia) {
    const familiaItems = await fetchFamiliaItems(
      familia,
      pessoas,
      evento,
      familiaCache
    );
    const match = familiaItems.find(
      (item: any) =>
        Number(item.id ?? item.Produto?.id) === Number(produtoId)
    );

    if (match && isApiProductActive(match)) {
      const unitPrice = calcUnitPriceFromFamiliaItem(match);
      return {
        unitPrice: unitPrice > 0 ? unitPrice : null,
        disponivel: true,
        familia,
        nome:
          match.Nome ||
          match.Produto?.Nome ||
          nomeOriginal ||
          `Produto #${produtoId}`,
        image:
          match?.Imagem?.url ||
          match?.Produto?.Imagem?.url ||
          imageOriginal,
      };
    }
  }

  if (!catalogRecord) {
    catalogRecord = await fetchProdutoFromCatalog(
      produtoId,
      catalogCache,
      catalogPages
    );
    if (!familia) {
      familia = resolveFamiliaHint(produto, catalogRecord);
    }
  }

  if (catalogRecord && isApiProductActive(catalogRecord)) {
    const valor = Number(catalogRecord.valor ?? catalogRecord.Valor ?? 0);
    return {
      unitPrice: valor > 0 ? valor : null,
      disponivel: true,
      familia: familia || extractFamiliaFromProdutoRecord(catalogRecord),
      nome: catalogRecord.Nome || nomeOriginal || `Produto #${produtoId}`,
      image: catalogRecord?.Imagem?.url || imageOriginal,
    };
  }

  return {
    unitPrice: null,
    disponivel: false,
    familia,
    nome: formatNomeProdutoIndisponivel(nomeOriginal, produtoId),
    image: imageOriginal,
  };
}

async function fetchServicosAtualizados(distanciaKm: number): Promise<any[]> {
  const cleanDistance = parseFloat(String(distanciaKm)) || 0;
  const copeiraOverrideValue = getCopeiraOverride();

  const servicosQuery: Record<string, string> = {
    distancia: cleanDistance.toString(),
    ...(copeiraOverrideValue && { copeira_override: copeiraOverrideValue }),
  };

  let servicesData = await getJson<any>('rEsr7KLt', 'servicos', servicosQuery);

  if (!Array.isArray(servicesData) || servicesData.length === 0) {
    try {
      let prodUrl = `https://xnhv-gl4r-wqdk.b2.xano.io/api:rEsr7KLt/servicos?distancia=${cleanDistance}`;
      if (copeiraOverrideValue) {
        prodUrl += `&copeira_override=${encodeURIComponent(copeiraOverrideValue)}`;
      }
      const prodResponse = await fetch(prodUrl);
      if (prodResponse.ok) {
        const prodData = await prodResponse.json();
        if (Array.isArray(prodData) && prodData.length > 0) {
          servicesData = prodData;
        }
      }
    } catch {
      /* mantém vazio */
    }
  }

  if (!Array.isArray(servicesData)) {
    servicesData = [];
  }

  return servicesData;
}

function findServicePrice(
  servicesData: any[],
  tipo: 'copeira' | 'utensilios'
): number | null {
  const matcher =
    tipo === 'copeira'
      ? (s: any) =>
          s.Nome?.toLowerCase().includes('copeira') ||
          s.Tipo?.toLowerCase().includes('copeira') ||
          s.id === 2 ||
          s.id === 3
      : (s: any) =>
          s.Nome?.toLowerCase().includes('utensilio') ||
          s.Nome?.toLowerCase().includes('utensílio') ||
          s.Tipo?.toLowerCase().includes('utensilio') ||
          s.id === 4 ||
          s.id === 5;

  const service = servicesData.find(matcher);
  if (!service) return null;
  const valor = Number(service.Valor ?? 0);
  return Number.isFinite(valor) ? valor : null;
}

function isCopeiraServico(servicoId: number): boolean {
  return servicoId === 2 || servicoId === 3;
}

function isUtensiliosServico(servicoId: number): boolean {
  return servicoId === 4 || servicoId === 5;
}

/**
 * Atualiza preços de produtos e serviços nos eventos de duplicação
 * consultando a tabela de preços atual (produtoFamilia + servicos).
 */
export async function refreshDuplicacaoEventosPrecos(
  eventos: any[]
): Promise<any[]> {
  if (!Array.isArray(eventos) || eventos.length === 0) {
    return eventos;
  }

  const catalogCache = new Map<number, any | null>();
  const catalogPages = new Map<number, any[]>();
  const familiaCache = new Map<string, any[]>();
  const servicosCache = new Map<string, any[]>();

  const eventosAtualizados = [];

  for (const evento of eventos) {
    const pessoas = Number(evento?.pessoas || 0);
    const eventoApi = resolveEventoApiName(evento);
    const distanciaKm = parseFloat(String(evento?.km || '0')) || 0;

    const distanciaKey = String(distanciaKm);
    if (!servicosCache.has(distanciaKey)) {
      servicosCache.set(
        distanciaKey,
        await fetchServicosAtualizados(distanciaKm)
      );
    }
    const servicesData = servicosCache.get(distanciaKey) || [];
    const copeiraPrice = findServicePrice(servicesData, 'copeira');
    const utensiliosPrice = findServicePrice(servicesData, 'utensilios');

    const produtosAtualizados = [];
    for (const produto of evento?.produtos || []) {
      const produtoId = Number(produto?.produto_id);
      const quantity = Number(produto?.quantity || 0);
      let unitPrice = Number(produto?.unitPrice || produto?.price || 0);
      let disponivel = produto?.disponivel !== false;
      let nome = produto?.nome || produto?.name || '';
      let image = produto?.image || produto?.imagem || '';
      let familia = produto?.familia || '';

      if (produtoId && quantity > 0) {
        const resolved = await resolveProdutoDuplicacao(
          produto,
          pessoas,
          eventoApi,
          catalogCache,
          catalogPages,
          familiaCache
        );

        disponivel = resolved.disponivel;
        nome = resolved.nome;
        image = resolved.image || image;
        familia = resolved.familia || familia;

        if (resolved.unitPrice !== null && resolved.unitPrice > 0) {
          unitPrice = resolved.unitPrice;
        }
      }

      produtosAtualizados.push({
        ...produto,
        nome,
        name: nome,
        image,
        familia,
        disponivel,
        unitPrice,
        price: unitPrice,
        total: roundMoney(unitPrice * quantity),
      });
    }

    const servicosAtualizados = (evento?.servicos || []).map((servico: any) => {
      const servicoId = Number(servico?.servico_id);
      const quantity = Number(servico?.quantity || 0);
      let unitPrice = Number(servico?.unitPrice || servico?.price || 0);

      if (isCopeiraServico(servicoId) && copeiraPrice !== null) {
        unitPrice = copeiraPrice;
      } else if (isUtensiliosServico(servicoId) && utensiliosPrice !== null) {
        unitPrice = utensiliosPrice;
      }

      return {
        ...servico,
        unitPrice,
        price: unitPrice,
        total: roundMoney(unitPrice * quantity),
      };
    });

    eventosAtualizados.push({
      ...evento,
      produtos: produtosAtualizados,
      servicos: servicosAtualizados,
    });
  }

  return eventosAtualizados;
}
